import { MomentHelper } from 'src/app/core/models/moment-helper';
import {
    ActivitiesMetadataForm,
    ActivityFiltersForm,
    ActivityFiltersFormPageHack,
    ActivityToCreate,
    Activity,
    FilteredActivitiesResponse,
    ActivitySortField,
} from 'src/app/core/models/activity';
import { ChartInterval, ChartTile } from 'src/app/core/models/chart';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, tap, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Chart } from 'src/app/core/models/chart';
import { BarChartVM, ChartModel, ChartModelDetail, ChartVM, PieChartVM } from 'src/app/home/dashboard/chart-vm';
import { ActivityRepository } from '@/core/repositories/activity.repository';
import { HttpClient } from '@angular/common/http';
import { ChartDataLayerType, ChartDataTileType, ChartDataType, ChartViewResponseForm } from 'src/app/core/models/dtos';

@Injectable({ providedIn: 'root' })
export class ActivityService extends ApiService {
    static readonly ACTIVITY_MAX = 50000;
    private activitiesUpdate = new BehaviorSubject<void>(undefined);
    public activitiesUpdate$ = this.activitiesUpdate.asObservable();

    constructor(
        private activityRepository: ActivityRepository,
        http: HttpClient,
    ) {
        super(http);
    }

    getChart(
        layerIndex: number,
        start: Date,
        end: Date,
        intervalType: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR',
        userIds?: number[],
    ): Observable<ChartVM> {
        const request = {
            startDate: MomentHelper.dateWithoutTimezone(start),
            endDate: MomentHelper.dateWithoutTimezone(end),
            intervalType,
            userIds,
            strictDate: false,
            note: undefined,
            billed: undefined,
            undefined,
            groups: undefined,
        };

        return this._http.post<ChartViewResponseForm>(ApiService.openApiUrl + `chart/filter`, request).pipe(
            // bisherige Chart API: chart  neuer test: chart/filter
            map((response: ChartViewResponseForm): ChartVM => {
                const intervals: [string, ChartDataType][] = Object.entries(response.intervals);

                const chartIntervals = intervals.map((x: [string, ChartDataType]) => {
                    const responseLayers: ChartDataLayerType = x[1][layerIndex];

                    const tileEntries: [string, ChartDataTileType][] = Object.entries(responseLayers?.tiles ?? {});
                    const chartTiles = tileEntries.map((tile: [string, ChartDataTileType]) => {
                        const color: string = tile[1]?.color ?? '';
                        const tileSum: number = tile[1]?.tileSum ?? 0;

                        const chartTile: ChartTile = {
                            tileSum,
                            name: tile[0],
                            color,
                        };
                        return chartTile;
                    });

                    const chartInterval: ChartInterval = {
                        layerSum: responseLayers?.layerSum ?? 0,
                        date: x[0],
                        tiles: !chartTiles ? [] : chartTiles,
                    };
                    return chartInterval;
                });

                const chart: Chart = {
                    totalSum: response.totalSum,
                    intervalType: response.intervalType,
                    intervals: chartIntervals,
                };

                const chartModels: ChartModel[] = [];
                chart.intervals.forEach((i) => {
                    const details: ChartModelDetail[] = [];
                    i.tiles.forEach((tile) => {
                        details.push({
                            color: tile.color,
                            tileSum: tile.tileSum,
                            name: tile.name,
                        });
                    });

                    chartModels.push({
                        date: i.date,
                        details,
                    });
                });

                const names = new Set<string>();
                chartModels.forEach((model) => {
                    model.details.forEach((detail) => {
                        names.add(detail.name);
                    });
                });

                const barCharts: BarChartVM[] = [];
                names.forEach((name) => {
                    const data: number[] = [];
                    let color = '';
                    let borderColor = '#bbb';

                    chartModels.forEach((model) => {
                        if (model.details.map((d) => d.name).includes(name)) {
                            const detail = model.details.find((d) => d.name === name);
                            if (detail) {
                                const value = +(detail.tileSum / 3600).toFixed(2);
                                data.push(value);
                                color = detail.color.slice(0, -2); // detail.color
                                if (color != '#ffffff') {
                                    borderColor = color;
                                }
                            }
                        } else {
                            data.push(0);
                        }
                    });

                    barCharts.push({
                        label: name,
                        backgroundColor: color, //  Transparency mit   backgroundColor: color + 80
                        borderWidth: 1,
                        // borderRadius: 2,
                        borderColor: borderColor, //    #ffeeeeee    color
                        borderSkipped: false,
                        borderRadiusOnAllStackedSeries: false,
                        data,
                    });
                });

                let fav = '';
                const numbers: number[] = [];
                barCharts.forEach((cvm) => {
                    const value = cvm.data.reduce((a, b) => a + b, 0);
                    const found = numbers.find((num) => num > value);
                    if (found === undefined) {
                        numbers.push(value);
                        fav = cvm.label;
                    }
                });

                const pieData: number[] = [];
                const backgroundColor: string[] = [];
                names.forEach((name) => {
                    barCharts.forEach((barChart) => {
                        if (name === barChart.label) {
                            pieData.push(barChart.data.reduce((a, b) => a + b, 0));
                            backgroundColor.push(barChart.backgroundColor);
                        }
                    });
                });

                const pieChart: PieChartVM = {
                    labels: [...names],
                    data: pieData,
                    backgroundColor,
                };

                const chartvm: ChartVM = {
                    dates: chart.intervals.map((i) => i.date),
                    totalSum: chart.totalSum,
                    highestName: fav,
                    barChart: barCharts,
                    pieChart,
                    tableData: [],
                };

                return chartvm;
            }),
        );
    }

    /**
     * Gets the count of activities of the current user
     * For regular users, this is the count of activities of themselves
     * For admins and managers, this is the count of activities of all users of the account.
     * @returns The activity count
     */
    public getActivityCount(): Observable<number> {
        return this.activityRepository.getActivityCount();
    }

    public async getActivityCountAsync(): Promise<number> {
        return await firstValueFrom(this.activityRepository.getActivityCount());
    }

    public async getFilteredActivitiesAsync(
        filterForAPI: ActivityFiltersFormPageHack,
        sortField?: ActivitySortField,
        sortOrder?: number,
    ): Promise<FilteredActivitiesResponse> {
        return firstValueFrom(this.getFilteredActivities(filterForAPI, sortField, sortOrder));
    }

    public getFilteredActivities(
        filterForAPI: ActivityFiltersFormPageHack,
        sortField?: ActivitySortField,
        sortOrder?: number,
    ): Observable<FilteredActivitiesResponse> {
        // TODO pageNumber and pageSize should not be part of the filterFotAPI object, also the type is missing!
        const pageNumber = filterForAPI.pageNumber || 0;
        const pageSize = filterForAPI.pageSize || ActivityService.ACTIVITY_MAX;
        // remove pageNumber and pageSize from the filter
        delete filterForAPI.pageNumber;
        delete filterForAPI.pageSize;

        if (!sortField) {
            sortField = 'local_start_date';
        }
        if (sortOrder === undefined) {
            sortOrder = 0;
        }

        return this.activityRepository.getFilteredActivities(pageNumber, pageSize, filterForAPI, sortField, sortOrder);
    }

    public fetchActivitiesMeta(filterForAPI?: ActivityFiltersForm): Observable<ActivitiesMetadataForm> {
        return this.activityRepository.fetchActivitiesMeta(filterForAPI);
    }

    public delete(id: number): Observable<void> {
        return this.withUpdateTrigger(this.activityRepository.deleteById(id));
    }

    public deleteBulk(ids: number[]): Observable<void> {
        return this.withUpdateTrigger(this.activityRepository.deleteByIds(ids));
    }

    public billedEditBulk(ids: number[], billed: boolean): Observable<void> {
        return this.withUpdateTrigger(this.activityRepository.billedEditBulk(ids, billed));
    }

    public create(activity: ActivityToCreate): Observable<Activity> {
        return this.withUpdateTrigger<Activity>(this.activityRepository.create(activity));
    }

    public update(activity: Activity): Observable<void> {
        return this.withUpdateTrigger(this.activityRepository.update(activity));
    }

    private withUpdateTrigger<T = void>(obs: Observable<T>): Observable<T> {
        return obs.pipe(tap(() => this.triggerActivitiesUpdate()));
    }

    private triggerActivitiesUpdate(): void {
        this.activitiesUpdate.next(); // Trigger a fetch for new data
    }
}
